/* Custom Wishlist Logic */
document.addEventListener('DOMContentLoaded', () => {
  const LOCAL_STORAGE_KEY = 'prishious_wishlist';
  const EVENT_NAME = 'wishlist:updated';
  
  class CustomWishlist {
    constructor() {
      this.items = [];
      this.init();
    }

    init() {
      const cw = window.CustomerWishlist || {};
      const isLoggedIn = !!cw.isLoggedIn;
      // customer.id from Liquid (number) or null for guests. Stored as a string so it can
      // tag which account this device's localStorage belongs to.
      const customerId = cw.customerId != null ? String(cw.customerId) : null;

      // 1. Load from localStorage. New shape is { owner, items }; older builds stored a bare
      //    array — readLocal() normalises both. `owner` = the customer id this device last
      //    synced as (or null for guest / legacy data).
      const local = this.readLocal();

      // 2. Load from Cloud (customer metafield, rendered into window.CustomerWishlist by
      //    theme.liquid). Already a parsed array (or null when empty / logged out).
      const cloudData = Array.isArray(cw.cloudData) ? cw.cloudData : [];

      // 3. Decide the authoritative list.
      this.owner = local.owner;
      let needsSync = false;

      if (isLoggedIn && customerId) {
        if (local.owner === customerId) {
          // This device already belongs to this customer → the cloud is the source of truth.
          // Taking it verbatim (NOT a union with local) is what makes deletions made on
          // another device actually stick here; a union would resurrect them.
          this.items = cloudData.slice();
        } else if (local.owner == null) {
          // Guest data (or first load after this update) → one-time union merge so items a
          // visitor saved before logging in aren't lost, then claim this device for the
          // customer. Future loads take the cloud-authoritative branch above.
          this.items = this.mergeUnique(local.items, cloudData);
          needsSync = this.items.length !== cloudData.length;
        } else {
          // localStorage belongs to a DIFFERENT customer (shared device) → never merge one
          // account's list into another. Cloud wins; drop the other account's local copy.
          this.items = cloudData.slice();
        }
        this.owner = customerId;
      } else {
        // Guest: device-local only. Keep any existing owner tag so logging back into the
        // same account is still recognised as "already synced" rather than re-merging.
        this.items = local.items.slice();
      }

      // 4. Persist the resolved list (with its owner tag) back to localStorage.
      this.saveToLocal();

      // 5. Bind events and scan DOM
      this.bindEvents();
      this.scanDOM();

      // Dispatch initial event for counters
      this.dispatchUpdate();

      // 6. If a guest→login merge produced items the cloud didn't have, push them up.
      if (needsSync) this.syncToServer();
    }

    readLocal() {
      try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!stored) return { owner: null, items: [] };
        const parsed = JSON.parse(stored);
        // Legacy format: a bare array of items, no owner → treat as guest data.
        if (Array.isArray(parsed)) return { owner: null, items: parsed };
        return {
          owner: parsed && parsed.owner != null ? String(parsed.owner) : null,
          items: parsed && Array.isArray(parsed.items) ? parsed.items : []
        };
      } catch (e) {
        console.error('Error reading localStorage wishlist:', e);
        return { owner: null, items: [] };
      }
    }

    mergeUnique(localItems, cloudItems) {
      // Union by handle; cloud entries take precedence (fresher metadata).
      const map = new Map();
      (localItems || []).forEach(item => { if (item && item.handle) map.set(item.handle, item); });
      (cloudItems || []).forEach(item => { if (item && item.handle) map.set(item.handle, item); });
      return Array.from(map.values());
    }

    saveToLocal() {
      try {
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify({ owner: this.owner || null, items: this.items })
        );
      } catch (e) {
        console.error('Error saving to localStorage:', e);
      }
    }

    syncToServer() {
      // Debounce writes: a customer toggling several items quickly results in ONE write,
      // not one per click. This keeps us well under Shopify's Admin API write rate limit
      // at scale. The actual send happens in flushSync().
      if (!window.CustomerWishlist || !window.CustomerWishlist.isLoggedIn) return;
      this._dirty = true;
      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(() => this.flushSync(), 1200);
    }

    flushSync(useBeacon) {
      // Persist the wishlist to the customer's Shopify metafield via the App Proxy.
      // The proxy is signed by Shopify and supplies the authoritative logged_in_customer_id,
      // so we never send a customer id from the client. Guests are skipped.
      if (!window.CustomerWishlist || !window.CustomerWishlist.isLoggedIn) return;
      if (!this._dirty) return;
      clearTimeout(this._syncTimer);
      this._dirty = false;

      const body = JSON.stringify({ items: this.items });

      // On page exit, fetch may be cancelled — sendBeacon reliably delivers the final state.
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon('/apps/wishlist', new Blob([body], { type: 'application/json' }));
        return;
      }

      fetch('/apps/wishlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true
      }).catch((err) => console.error('Wishlist cloud sync failed:', err));
    }

    toggleItem(productData, buttonEl) {
      const index = this.items.findIndex(item => item.handle === productData.handle);
      const isActive = index > -1;

      if (isActive) {
        // Remove item
        this.items.splice(index, 1);
      } else {
        // Add item
        this.items.push(productData);
      }

      this.saveToLocal();
      this.syncToServer();
      this.dispatchUpdate();
      
      // Optimistically update all buttons on page for this handle
      this.updateButtons(productData.handle, !isActive);
    }

    scanDOM() {
      const buttons = document.querySelectorAll('[data-wishlist-btn]');
      buttons.forEach(btn => {
        const handle = btn.getAttribute('data-product-handle');
        const isActive = this.items.some(item => item.handle === handle);
        this.updateButtonUI(btn, isActive);
      });
    }

    updateButtons(handle, isActive) {
      const buttons = document.querySelectorAll(`[data-wishlist-btn][data-product-handle="${handle}"]`);
      buttons.forEach(btn => this.updateButtonUI(btn, isActive));
    }

    updateButtonUI(btn, isActive) {
      if (isActive) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        const label = btn.querySelector('.wishlist-btn__label');
        if (label) label.textContent = 'In Wishlist';
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
        const label = btn.querySelector('.wishlist-btn__label');
        if (label) label.textContent = 'Add to Wishlist';
      }
    }

    bindEvents() {
      // Flush any debounced write before the page goes away, so a quick toggle-then-leave
      // isn't lost. pagehide covers navigation/close; visibilitychange covers tab switches
      // and mobile backgrounding (where pagehide can be unreliable).
      window.addEventListener('pagehide', () => this.flushSync(true));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flushSync(true);
      });

      // Event delegation on the body for dynamic quick view / infinite scroll support
      document.body.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wishlist-btn]');
        if (!btn) return;
        
        e.preventDefault();
        e.stopPropagation();

        const productData = {
          handle: btn.getAttribute('data-product-handle'),
          title: btn.getAttribute('data-product-title'),
          img: btn.getAttribute('data-product-img'),
          price: btn.getAttribute('data-product-price'),
          url: btn.getAttribute('data-product-url') || `/products/${btn.getAttribute('data-product-handle')}`
        };

        if (productData.handle) {
          this.toggleItem(productData, btn);
        }
      });
    }

    dispatchUpdate() {
      const event = new CustomEvent(EVENT_NAME, {
        detail: { items: this.items, count: this.items.length }
      });
      document.dispatchEvent(event);
    }
  }

  // Initialize and attach to window
  window.CustomerWishlistSystem = new CustomWishlist();
});
